import { Avatar, Flex } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { MRT_ColumnDef } from 'mantine-react-table'
import React, { useCallback, useMemo } from 'react'
import UserPageList from 'src/containers/Users'
import { formatDateTime, size } from 'src/variable/constant'
import { useLocation, useNavigate } from 'react-router-dom'
import { RemoveCircle, CheckCircle } from '@mui/icons-material'
import dayjs from 'dayjs'
import OwnerConfirmedCell from './OwnerConfirmedCell'
import TickSuggestedBadge from './TickSuggestedBadge'
import type { UserOwnerVerification } from 'src/utils/mapUserListRows'

const ellipsisStyle = (maxWidth: number): React.CSSProperties => ({
	maxWidth,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
})

interface UsersPageProps {
	showManagementActions?: boolean
	showStatusFilter?: boolean
	showSearchFieldSelector?: boolean
	locationFilter?: {
		field: 'country' | 'province'
		value: string
	}
}

function UsersPage({
	showManagementActions = true,
	showStatusFilter = true,
	showSearchFieldSelector = true,
	locationFilter,
}: UsersPageProps) {
	const navigate = useNavigate()
	const location = useLocation()
	const isTouchDevice = useMediaQuery('(hover: none) and (pointer: coarse)')

	const renderTextCell = (value: string | null | undefined, maxWidth: number) => {
		const text = value?.trim() || '-'
		return <div style={ellipsisStyle(maxWidth)} title={text}>{text}</div>
	}

	const columns: MRT_ColumnDef[] = useMemo(
		() => [
			{
				accessorKey: 'no',
				header: 'No',
				size: size.zero,
			},
			{
				accessorKey: 'name',
				header: 'Name',
				size: size.zero,
				maxSize: size.text,
				Cell: ({ cell }) => renderTextCell(cell.getValue<string>(), size.text),
			},
			{
				accessorKey: 'avatar',
				header: 'Avatar',
				size: size.zero,
				Cell: ({ cell }) => {
					const src = cell.getValue<string>()
					if (!src) {
						return null
					}
					return <Avatar src={src} radius={50} size={isTouchDevice ? 32 : 40} />
				},
			},
			{
				accessorKey: 'phone',
				header: 'Phone',
				size: size.zero,
				maxSize: size.text,
			},
			{
				accessorKey: 'email',
				header: 'Email',
				size: size.zero,
				maxSize: size.text,
				Cell: ({ cell }) => renderTextCell(cell.getValue<string>(), size.text),
			},
			{
				accessorKey: 'address',
				header: 'GPS',
				size: size.zero,
				maxSize: size.text,
				Cell: ({ cell }) => renderTextCell(cell.getValue<string>(), size.medium),
			},
			{
				accessorKey: 'address_local',
				header: 'Address Local',
				size: size.zero,
				maxSize: size.text,
				Cell: ({ cell }) => renderTextCell(cell.getValue<string>(), size.medium),
			},
			{
				accessorKey: 'invite_code',
				header: 'Invite code',
				size: size.zero,
				maxSize: size.text,
			},
			{
				accessorKey: 'version',
				header: 'Version',
				size: size.zero,
				maxSize: size.text,
				Cell: ({ cell }) => renderTextCell(cell.getValue<string>(), size.text),
			},
			{
				accessorKey: 'blocked_at',
				header: 'Status',
				size: size.zero,
				maxSize: size.text,
				Cell: ({ cell }) => {
					const value = cell.getValue<string>()
					const Icon = value ? RemoveCircle : CheckCircle
					const label = value ? 'Blocked' : 'Available'
					return <Icon color={value ? 'error' : 'success'} fontSize="small" titleAccess={label} />
				},
			},
			{
				accessorKey: 'unblocked_at',
				header: 'Block expires',
				size: size.zero,
				maxSize: size.text,
				Cell: ({ cell, row }) => {
					const unblockedAt = cell.getValue<string>()
					const blockedAt = (row.original as any)?.blocked_at
					let label = '-'
					if (blockedAt) {
						if (unblockedAt) {
							const parsed = dayjs(unblockedAt)
							label = parsed.isValid()
								? parsed.format(formatDateTime)
								: String(unblockedAt)
						} else {
							label = 'Permanent'
						}
					}

					return renderTextCell(label, size.text)
				},
			},
			{
				accessorKey: 'is_verified',
				header: 'Verified',
				size: size.zero,
				maxSize: size.text,
				Cell: ({ cell, row }) => {
					const value = cell.getValue<string>()
					const is_regular = row.original['is_regular']
					return (
						<>
							<div>{value ? 'UniVini user' : ''}</div>
							<div>{is_regular ? 'Regular user' : ''}</div>
							<TickSuggestedBadge
								suggested={row.original['tick_suggested']}
								level={row.original['xp_level']}
							/>
						</>
					)
				},
			},
			{
				accessorKey: 'created_at',
				header: 'Date join',
				size: size.zero,
				maxSize: size.text,
			},
			{
				accessorKey: 'owner_verification',
				header: 'Owner confirmed',
				size: size.zero,
				maxSize: size.text,
				Cell: ({ cell }) => (
					<OwnerConfirmedCell value={cell.getValue<UserOwnerVerification | null>()} />
				),
			},
		],
		[isTouchDevice],
	)

	const fields = [
		{
			value: 'name',
			label: 'Name',
		},
		{
			value: 'old_name',
			label: 'Old Name',
		},
		{
			value: 'email',
			label: 'Email',
		},
		{
			value: 'phone',
			label: 'Phone',
		},
		{
			value: 'invite_code',
			label: 'Invite code',
		},
		{
			value: 'country',
			label: 'Country',
		},
		{
			value: 'province',
			label: 'Province',
		},
	]
	const stateAccountOpts = [
		{
			label: 'All',
			value: 'All',
		},
		{
			label: 'Active',
			value: 'Active',
		},
		{
			label: 'Deleted',
			value: 'Deleted',
		},
		{
			label: 'Blocked',
			value: 'Blocked',
		},
		// UD-785: the server filters these by the same status parameter, live accounts only.
		{
			label: 'VIP active',
			value: 'VIP active',
		},
		{
			label: 'UniVini staff',
			value: 'UniVini staff',
		},
		{
			label: 'Regular',
			value: 'Regular',
		},
		// UD-784: XP level 6 or more and no green tick yet; staff turn Regular on by hand.
		{
			label: 'Suggested for tick',
			value: 'Suggested for tick',
		},
	]
	const handleDetail = useCallback((row) => {
		const returnTo = `${location.pathname}${location.search}`
		navigate(`/users/${row?.original?.id}?return_to=${encodeURIComponent(returnTo)}`)
	}, [location.pathname, location.search, navigate])
	return (
		<Flex sx={{ width: '100%' }}>
			<UserPageList
				columns={columns}
				fields={fields}
				selectOption={stateAccountOpts}
				handleDetail={handleDetail}
				showManagementActions={showManagementActions}
				showStatusFilter={showStatusFilter}
				showSearchFieldSelector={showSearchFieldSelector}
				locationFilter={locationFilter}
			/>
		</Flex>
	)
}

export default React.memo(UsersPage)
